import { FieldArray, Form, useFormikContext } from 'formik';

import type { PriceMessage } from '../../../../types/PricesMessage';
import type { ScheduleMessage } from '../../../../types/ScheduleMessage';
import { Button } from '../../../cdl/Button/Button';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { Box } from '../../../common/ui/Box';
import type { PriceCompareFormValues } from '../types/PriceCompareFormValues';
import { usePriceCompare } from '../usePriceCompare';
import { createPriceComparePort } from '../utils/createPriceComparePort';

import { useCompareSelectionState } from './hooks/useCompareSelectionState';
import { OverlayPortPriceInformation } from './OverlayPortPriceInformation';

interface OverlayPriceCompareFormProps {
    defaultContractId?: string;
    schedules: ScheduleMessage[];
    deliveryDate?: string;
}

export const OverlayPriceCompareForm = ({
    defaultContractId,
    schedules,
    deliveryDate,
}: OverlayPriceCompareFormProps) => {
    const role = useRole();
    const { values } = useFormikContext<PriceCompareFormValues>();
    const { isFetching, priceMap, contractsById } = usePriceCompare({
        values,
        defaultContractId,
        deliveryDate,
    });
    const { selection, setSelection, singleSupplierSelection } = useCompareSelectionState();

    const handleRemovePort = (portId: string | undefined, remove: () => void) => {
        remove();
        if (portId === selection.portId) {
            setSelection({ portId: null, supplierIds: [], contractIds: [] });
        }
    };

    const handleSelectPrice = (portId: string | undefined, priceInfo: PriceMessage) => {
        if (role.isSuperAdmin() || !portId) {
            return;
        }

        const { supplierId, contractId } = priceInfo;
        if (singleSupplierSelection) {
            const isSelected =
                selection.portId === portId &&
                selection.supplierIds.includes(supplierId) &&
                (!contractId || selection.contractIds.includes(contractId));
            setSelection({
                portId,
                supplierIds: isSelected ? [] : [supplierId],
                contractIds: !isSelected && contractId ? [contractId] : [],
            });
            return;
        }
        const next =
            selection.portId === portId
                ? { ...selection, supplierIds: [...selection.supplierIds], contractIds: [...selection.contractIds] }
                : { portId, supplierIds: [], contractIds: [] };

        if (contractId) {
            if (next.contractIds.includes(contractId)) {
                next.contractIds = next.contractIds.filter((id) => id !== contractId);
                const otherContractForSupplier = next.contractIds.some(
                    (id) => contractsById[id]?.supplierId === supplierId
                );
                if (!otherContractForSupplier) {
                    next.supplierIds = next.supplierIds.filter((id) => id !== supplierId);
                }
            } else {
                const existingContractForSupplier = next.contractIds.find(
                    (id) => contractsById[id]?.supplierId === supplierId
                );
                if (existingContractForSupplier) {
                    next.contractIds = next.contractIds.filter((id) => id !== existingContractForSupplier);
                }
                next.contractIds.push(contractId);
                if (!next.supplierIds.includes(supplierId)) {
                    next.supplierIds.push(supplierId);
                }
            }
        } else if (next.supplierIds.includes(supplierId)) {
            next.supplierIds = next.supplierIds.filter((id) => id !== supplierId);
        } else {
            next.supplierIds.push(supplierId);
        }

        setSelection(next);
    };

    return (
        <Form>
            <Box marginTop={24} padding={40} backgroundColor="background.default">
                <FieldArray name="portInformation">
                    {({ push, remove }) => (
                        <Box>
                            {values.portInformation.map((port, index) => (
                                <OverlayPortPriceInformation
                                    key={port.id}
                                    index={index}
                                    onRemove={() => handleRemovePort(port.portId, () => remove(index))}
                                    isFetching={isFetching}
                                    supplierData={port.portId ? priceMap?.[port.portId] : undefined}
                                    portId={port.portId}
                                    defaultContractId={defaultContractId}
                                    showRemoveIcon={values.portInformation.length > 1}
                                    onSelectBoxClick={handleSelectPrice}
                                    schedules={schedules}
                                />
                            ))}

                            <Box width="200px" style={{ flexShrink: 0 }}>
                                <Button
                                    type="button"
                                    LeadingIcon={IconPlus}
                                    onClick={() => push(createPriceComparePort())}
                                >
                                    {translate('compare.addPort')}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </FieldArray>
            </Box>
        </Form>
    );
};
